import { Button, Checkbox, Icon, Loading } from '@smartgift/frontend-toolkit';
import { useQueryClient } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import * as TableS from '@hero/components/molecules/Table/style';
import { contactServices } from '@hero/services';
import { cleanObjectEmptyFields, formatNumber } from '@hero/utils';

function CustomSelectionHead({ table }) {
  const { t } = useTranslation();
  const [toggleSelectAll, setToggleSelectAll] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const queryClient = useQueryClient();

  const showingSelectedItems = useMemo(() => {
    return table.getState()?.showingSelectedItems;
  }, [table.getState()?.showingSelectedItems]);

  const globalFilters = useMemo(() => {
    return table.getState()?.globalFilters;
  }, [table.getState()?.globalFilters]);

  const rowSelection = useMemo(() => {
    return table.getState()?.rowSelection;
  }, [table.getState()?.rowSelection]);

  const totalRowCount = useMemo(() => {
    return table.getState()?.pagination?.total;
  }, [table.getState()?.pagination?.total]);

  const selectedRowCount = useMemo(() => {
    return Object.keys(rowSelection || {}).length;
  }, [rowSelection]);

  const onToggleSelectAll = async () => {
    if (toggleSelectAll) {
      table.resetRowSelection();
    } else {
      const searchTerm = globalFilters.searchTerm;
      const tags = globalFilters.filters?.map(filter => filter.value);
      const ids = showingSelectedItems ? Object.keys(rowSelection) : undefined;

      setIsLoading(true);
      const data = await queryClient.fetchQuery(['selectAll', searchTerm, tags, ids], () => {
        return contactServices.selectAllTags(
          cleanObjectEmptyFields({
            filters: {
              searchTerm: globalFilters.searchTerm,
              tags: globalFilters.filters?.map(filter => filter.value),
              ids: showingSelectedItems ? Object.keys(rowSelection) : undefined
            }
          })
        );
      });

      if (data?.docs?.length)
        table.setRowSelection(
          data.docs.reduce((result, doc) => ({ ...result, [doc.id]: true }), {})
        );

      setIsLoading(false);
    }

    setToggleSelectAll(!toggleSelectAll);
  };

  React.useEffect(() => {
    if (selectedRowCount === 0) {
      setToggleSelectAll(false);
    }
    if (selectedRowCount === totalRowCount) {
      setToggleSelectAll(true);
    }
  }, [selectedRowCount, totalRowCount]);

  return (
    <TableS.HeadSelected>
      <TableS.HeadCheckbox>
        <Checkbox
          {...{
            id: 'selectAll',
            checked: table.getIsAllRowsSelected(),
            indeterminate: table.getIsSomeRowsSelected(),
            onChange: table.getToggleAllRowsSelectedHandler()
          }}
        />
      </TableS.HeadCheckbox>
      <TableS.HeadSelectedItems>
        <Icon name="tag" px={32} space={7} />
        <Trans i18nKey="ofItemSelected" values={{ count: selectedRowCount }} />
        {!(!toggleSelectAll && totalRowCount - selectedRowCount <= 0) && (
          <Button color="neutral" variant="link" onClick={onToggleSelectAll}>
            {t(toggleSelectAll ? 'clearSelection' : 'selectRemainingNType', {
              totalContactCount: formatNumber(totalRowCount - selectedRowCount),
              type: t('tags').toLocaleLowerCase()
            })}
          </Button>
        )}
        {isLoading && <Loading size="sm" type="ring" />}
      </TableS.HeadSelectedItems>
    </TableS.HeadSelected>
  );
}

export { CustomSelectionHead };
